// Tools menu on the Legal Case Tracker — everything that is not the day's work lives here (Dean, 13 Oct 2026 noise pass).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Grid3x3, Loader2, Settings2, Wrench, History, Users, FileSpreadsheet, RefreshCw, GitMerge, Wand2, Scale } from "lucide-react";
import { toast } from "sonner";
import { getLegalHousekeeping, runLegalHousekeeping } from "@/lib/api";
import LoanFamiliesDialog from "./LoanFamiliesDialog";
import NormaliseNamesDialog from "./NormaliseNamesDialog";
import ReferralCandidatesTable from "./ReferralCandidatesTable";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "—");

export function TrackerToolsMenu({ canPublish, isLegalHead, onExportCsv, exportDisabled, onRefreshAll, refreshRunning, autoRefreshRun, onCasesChanged }) {
  const navigate = useNavigate();
  const [hkOpen, setHkOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [famOpen, setFamOpen] = useState(false);
  const [normOpen, setNormOpen] = useState(false);
  const [refOpen, setRefOpen] = useState(false);
  const run = async () => {
    setRunning(true);
    try {
      const r = await runLegalHousekeeping();
      toast.success(r.already_running ? "Housekeeping is already running" : "Housekeeping started — one customer one file · LoanDisk reconcile · names · default owners. Open the log to follow it.");
      setHkOpen(true);
    } catch (e) { toast.error("Housekeeping failed to start: " + (e?.response?.data?.detail || e.message)); }
    finally { setRunning(false); }
  };
  const auto = autoRefreshRun ? `${autoRefreshRun.status === "success" ? "✓" : autoRefreshRun.status === "running" ? "…" : "✗"} nightly ${autoRefreshRun.date || ""}` : "";
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="legal-tools-menu"><Wrench className="w-3.5 h-3.5 mr-1.5" /> Tools</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="text-xs w-72">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Reports</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => navigate("/legal/audit-matrix")} data-testid="legal-tools-audit-matrix"><Grid3x3 className="w-3.5 h-3.5 mr-2" /> Audit matrix</DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/legal/handler-report")} data-testid="legal-tools-handler-report"><Users className="w-3.5 h-3.5 mr-2" /> Handler report</DropdownMenuItem>
          <DropdownMenuItem onClick={onExportCsv} disabled={exportDisabled} data-testid="legal-export-csv"><FileSpreadsheet className="w-3.5 h-3.5 mr-2" /> Export this view (CSV)</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Data</DropdownMenuLabel>
          <DropdownMenuItem onClick={onRefreshAll} disabled={refreshRunning} data-testid="legal-refresh-all" title="Pull every legal case from LoanDisk, re-import Airtable CaseNotes and sync OneDrive metadata (runs nightly by itself)">
            {refreshRunning ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-2" />}
            <span className="flex-1">Refresh All (LoanDisk · Airtable · OneDrive)</span>
            {auto && <span className="ml-2 text-[10px] text-muted-foreground" data-testid="legal-auto-refresh-chip">{auto}</span>}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={run} disabled={!canPublish || running} data-testid="legal-tools-housekeeping-run" title={canPublish ? "" : "Publisher permission needed"}>
            {running ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-2" />} Run housekeeping now
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setHkOpen(true)} data-testid="legal-tools-housekeeping-log"><History className="w-3.5 h-3.5 mr-2" /> Housekeeping log</DropdownMenuItem>
          {isLegalHead && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Head of Legal</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setRefOpen(true)} data-testid="legal-tools-referrals"><Scale className="w-3.5 h-3.5 mr-2" /> Referral candidates</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFamOpen(true)} data-testid="legal-loan-families-btn"><GitMerge className="w-3.5 h-3.5 mr-2" /> Loan families</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setNormOpen(true)} data-testid="legal-normalise-names-btn"><Wand2 className="w-3.5 h-3.5 mr-2" /> Normalise names</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/legal/timelines")} data-testid="legal-tools-settings"><Settings2 className="w-3.5 h-3.5 mr-2" /> Legal settings</DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <HousekeepingDialog open={hkOpen} onClose={() => setHkOpen(false)} />
      {isLegalHead && (
        <>
          <NormaliseNamesDialog open={normOpen} onClose={() => setNormOpen(false)} onApplied={onCasesChanged} />
          <LoanFamiliesDialog open={famOpen} onClose={() => setFamOpen(false)} onApplied={onCasesChanged} />
          <Dialog open={refOpen} onOpenChange={(o) => { if (!o) setRefOpen(false); }}>
            <DialogContent className="sm:max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="referral-candidates-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Scale className="w-4 h-4 text-red-700" /> Referral candidates</DialogTitle>
                <DialogDescription className="text-xs">Collections files meeting the legal referral triggers — recommend only. Hand to Legal from here goes through the same door as every other file.</DialogDescription>
              </DialogHeader>
              <ReferralCandidatesTable onReferred={onCasesChanged} />
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  );
}

function HousekeepingDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const load = () => getLegalHousekeeping({ limit: 100 }).then(d => alive && setData(d)).catch(() => {}).finally(() => alive && setLoading(false));
    setLoading(true); load();
    const t = setInterval(load, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [open]);
  const runs = data?.runs || [], log = data?.log || [];
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="housekeeping-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-4 h-4 text-red-700" /> Nightly housekeeping</DialogTitle>
          <DialogDescription className="text-xs">Runs at 01:00 London: one customer one file (merge / re-point) · LoanDisk status reconcile (LD Legal → route starts) · owner-name spelling · empty account handler ← loan officer. Never moves a diary date, never edits a note, never changes a hand-set owner.</DialogDescription>
        </DialogHeader>
        {loading && !data ? <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> : (
          <div className="space-y-3 text-xs">
            <div>
              <div className="font-semibold mb-1">Recent runs</div>
              {runs.length === 0 ? <div className="text-muted-foreground italic" data-testid="housekeeping-no-runs">No run yet.</div> : (
                <ul className="space-y-1" data-testid="housekeeping-runs">
                  {runs.slice(0, 6).map(r => (
                    <li key={r.run_id} className="flex items-center gap-2 border rounded px-2 py-1" data-testid={`housekeeping-run-${r.run_id}`}>
                      <span className={`font-semibold ${r.state === "done" ? "text-emerald-700" : r.state === "failed" ? "text-rose-700" : "text-amber-700"}`}>{r.state}{r.state === "running" ? ` · ${r.phase}` : ""}</span>
                      <span className="text-muted-foreground">{when(r.started_at)} · {r.trigger} · {r.by}</span>
                      <span className="ml-auto tabular-nums">{r.changes ?? 0} change{(r.changes ?? 0) === 1 ? "" : "s"}</span>
                      {r.error && <span className="text-rose-700 truncate max-w-[240px]" title={r.error}>{r.error}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="font-semibold mb-1">Change log</div>
              {log.length === 0 ? <div className="text-muted-foreground italic" data-testid="housekeeping-no-log">Nothing changed yet.</div> : (
                <ul className="space-y-0.5 max-h-80 overflow-y-auto pr-1" data-testid="housekeeping-log">
                  {log.map((l, i) => (
                    <li key={`${l.run_id}-${i}`} className="border-b last:border-0 py-1 flex gap-2">
                      <span className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">{when(l.at)}</span>
                      <span className="text-[10px] uppercase font-semibold text-slate-500 w-20 shrink-0">{l.kind}</span>
                      <span className="min-w-0"><span className="font-medium">{l.customer_name || ""}</span>{l.loan_application_id ? <span className="font-mono text-[10px] text-muted-foreground ml-1">{l.loan_application_id}</span> : null} <span className="text-slate-700">{l.detail}</span></span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
